/**
 * Rodapé do ChatWindow: atribuição, reply/edit, typing e input.
 */
import React from 'react';
import type { ChatMessage } from '../../hooks/useChat';
import type {
    ContactCardInput,
    LocationMessageInput,
    PixMessageInput,
} from '../../hooks/useChatSpecialActions';
import type { useChatAttachFlow } from '../../hooks/useChatAttachFlow';
import { ChatInput } from './ChatInput';
import { ReplyPreview } from './ReplyPreview';
import { EditPreview } from './EditPreview';
import { ChatAssignBanner } from './ChatAssignBanner';

type ChatAttachFlow = ReturnType<typeof useChatAttachFlow>;

interface ChatWindowFooterProps {
    conversationName: string;
    chatPlatform: 'whatsapp' | 'instagram';
    showAssignBanner: boolean;
    onAssignToMe?: () => void;
    replyingTo: ChatMessage | null;
    onCancelReply: () => void;
    editingMessage: ChatMessage | null;
    onCancelEdit: () => void;
    isPeerTyping?: boolean;
    messageText: string;
    setMessageText: (text: string) => void;
    sendValidationError: string | null;
    handleSend: () => void;
    attach: ChatAttachFlow;
    absorbComposerTextAsCaption: () => void;
    onSendMedia?: (file: File, caption?: string) => void;
    onSendContact?: (input: ContactCardInput) => Promise<void>;
    onSendLocation?: (input: LocationMessageInput) => Promise<void>;
    onSendPix?: (input: PixMessageInput) => Promise<void>;
    isPopup?: boolean;
}

export const ChatWindowFooter: React.FC<ChatWindowFooterProps> = ({
    conversationName,
    chatPlatform,
    showAssignBanner,
    onAssignToMe,
    replyingTo,
    onCancelReply,
    editingMessage,
    onCancelEdit,
    isPeerTyping,
    messageText,
    setMessageText,
    sendValidationError,
    handleSend,
    attach,
    absorbComposerTextAsCaption,
    onSendMedia,
    onSendContact,
    onSendLocation,
    onSendPix,
    isPopup,
}) => (
    <footer className="shrink-0 bg-card">
        {showAssignBanner && onAssignToMe && <ChatAssignBanner onAssign={onAssignToMe} />}
        {replyingTo && (
            <ReplyPreview
                message={replyingTo}
                conversationName={conversationName}
                onCancel={onCancelReply}
            />
        )}
        {editingMessage && <EditPreview message={editingMessage} onCancel={onCancelEdit} />}
        {isPeerTyping && (
            <p className="px-4 py-1 text-xs text-muted-foreground" aria-live="polite">
                Digitando...
            </p>
        )}
        <ChatInput
            chatPlatform={chatPlatform}
            messageText={messageText}
            setMessageText={setMessageText}
            sendValidationError={sendValidationError}
            handleSend={handleSend}
            attach={attach}
            absorbComposerTextAsCaption={absorbComposerTextAsCaption}
            onSendMedia={onSendMedia}
            onSendContact={onSendContact}
            onSendLocation={onSendLocation}
            onSendPix={onSendPix}
            isPopup={isPopup}
        />
    </footer>
);
